import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Departments } from '../../enums/departments.enum';
import { DateTime, Duration } from 'luxon';
import { selectSettings } from '../../../store/settings/settings.selectors';
import { SettingsState } from '../../../store/settings/settings.reducer';
import { Store } from '@ngrx/store';
import * as util from './../../../shared/services/util.service'
import { Subscription } from 'rxjs';
import { AppointmentBookingSource, AppointmentType, LocationType } from '@signal/asp-data-commons';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-dashboard-print-overlay',
  templateUrl: './dashboard-print-overlay.component.html',
  styleUrls: ['./dashboard-print-overlay.component.scss']
})
export class DashboardPrintOverlay implements OnInit,AfterViewInit, OnDestroy {
  department:Departments = Departments.SERVICE;
  printableData = [];
  private sub : Subscription;
  @ViewChild("print") print!: ElementRef;
  @ViewChild('formDialog') formDialog: ElementRef;
  salesApptTimeDuration = {
    "Test Drive": 60,
    "Delivery": 60,
    "Learning": 60,
    "Vehicle Inquiry": 60
  }

  constructor(public dialogRef: MatDialogRef<DashboardPrintOverlay>,
    private readonly settingsState: Store<SettingsState>,
    private readonly translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: any) {
      this.sub = this.settingsState.select(selectSettings).subscribe(data=>{
        this.salesApptTimeDuration['Test Drive'] = data.time.testdriveAppointmentDuration;
        this.salesApptTimeDuration.Delivery = data.time.deliveryAppointmentDuration + data.time.deliveryApptDuration;
        this.salesApptTimeDuration['Learning'] = data.time.techcenterAppointmentDuration;
        this.salesApptTimeDuration['Vehicle Inquiry'] = data.time.vehicleInquiryDuration;
      })
  }

  ngOnInit(): void {
    this.setupData();

    window.onafterprint = ()=>{
      this.dialogRef.close();
    }
  }

  getAppointmentCreatedBy(data){
    if (data.appointmentBookingSource === AppointmentBookingSource.ONEAPP || data.appointmentBookingSource === AppointmentBookingSource.ONEAPP_SP) {
        return 'serviceSchedule.oneApp';
    } else if (data.appointmentBookingSource === AppointmentBookingSource.ASP){
        return 'serviceSchedule.asp';
    } else if (data.appointmentBookingSource === "TCNA"){
        return 'serviceSchedule.tcna';
    } else if (data.appointmentBookingSource === "MSTC"){
        return 'serviceSchedule.mstc';
    } else if (data.appointmentCreatedByUserName) {
        return data.appointmentCreatedByUserName;
    } else if (data.appointmentCreatedBy) {
        return data.appointmentCreatedBy;
    } else {
        return '';
    }
  }

  sortAppointments(appointments) : Array<any>{
      if(appointments.length>1){
        appointments.sort((appt1,appt2)=>{
          const startDate1 = DateTime.fromFormat(`${appt1.appointmentStartDate} , ${appt1.timeSlotId}`,'yyyy-MM-dd , t');
          const startDate2 = DateTime.fromFormat(`${appt2.appointmentStartDate} , ${appt2.timeSlotId}`,'yyyy-MM-dd , t');
          return startDate1.toMillis() - startDate2.toMillis();
        })
      }
    return appointments;
  }
  
  setupData(){
    this.department = this.data.department;
    if (this.data?.appointmentDetails && Array.isArray(this.data.appointmentDetails) && this.data.appointmentDetails.length > 0) {
      this.data.appointmentDetails = this.sortAppointments(this.data.appointmentDetails);
      for(const appointment of this.data.appointmentDetails){

        let totalLaborHours = 0;
        let opCodeDescription = '';
        let opCodeList = '';

        if (appointment.serviceRequests && Array.isArray(appointment.serviceRequests)) {
          appointment.serviceRequests.forEach(serviceRequest => {
            if (serviceRequest?.details?.labourHours) {
              totalLaborHours += serviceRequest.details.labourHours;
            }
            if (serviceRequest?.opcode) {
              opCodeList = opCodeList !== '' ? `${opCodeList},<br>${serviceRequest.opcode}` : serviceRequest.opcode;
            }
            if (serviceRequest?.details?.description) {
              opCodeDescription = opCodeDescription !== '' ? `${opCodeDescription},${serviceRequest.details.description}` : serviceRequest.details.description;
            }
          });
        }
        let transportOption = '';
        if (appointment.transportation?.category) {
          transportOption = appointment.transportation.category === LocationType.DEALER ? 'pendingAppointments.dealership' : 'common.Remote';
        }

        const printableAppointmentData = {
          date: appointment.appointmentStartDate ? DateTime.fromISO(appointment.appointmentStartDate).toFormat('dd-MMM')?.toUpperCase() : '',
          time: appointment.timeSlotId || '',
          advisorName: appointment.advisor ? `${this.toTitleCase(appointment.advisor.firstName)} ${this.toTitleCase(appointment.advisor.lastName)}` : '',
          teamName: appointment?.teamName ? `Team ${this.toTitleCase(appointment.teamName)}` : '',
          estimatedDuration: this.department === "SALES" ? this.getTime((this.salesApptTimeDuration[appointment.appointmentType] ?? 60) * 0.0167) : `${this.getTime(totalLaborHours)}`,
          customerName: appointment.customer ? `${this.toTitleCase(appointment.customer.firstName)} ${this.toTitleCase(appointment.customer.lastName)}` : '',
          makeModelYear: appointment.appointmentType === AppointmentType.VEHICLE_INQUIRY ? (this.getVehicleForInquiry(appointment)) : (appointment.vehicle?.make ? `${util.getMakeModelYearText(appointment.vehicle)}`: 'NTV'),
          opCode: opCodeList || '',
          serviceDescription: opCodeDescription || '',
          TransportOption: transportOption,
          appointmentType: appointment.appointmentType || '',
          appointmentCreationDate: appointment.appointmentCreatedDate || '',
          appointmentCreatedBy: this.getAppointmentCreatedBy(appointment)
        }
        this.printableData.push(printableAppointmentData);
      }
    }

  }

  getVehicleForInquiry(appt) {
    if(!appt?.vehiclesOfInterest?.length && !appt?.vehicleType)
              {
                return this.translate.instant('vehicleInquiry.notSpecified');
              }
              else{
                const vehicleTypes=util.getVehicleTypeTraslation(appt?.vehicleType,this.translate);
                const vehicleDisplayText=util.getVehicleDisplayTextForEnquiryAppt(appt?.vehiclesOfInterest,this.translate);
                return `${vehicleTypes}${((vehicleTypes && vehicleDisplayText) ? ", " : '')}${vehicleDisplayText}`;
              }
  }

  private toTitleCase(text:string){
    return  text ? text.toString().charAt(0)?.toUpperCase()+text?.toString()?.substr(1)?.toLowerCase() : '';
  }
  private getTime(totalLaborHours){
    totalLaborHours = totalLaborHours * 60;
    const luxonSlotTime = Duration.fromObject({ minutes: totalLaborHours }).toFormat('hh:mm').split(":");
    return luxonSlotTime[0]+ 'hrs ' + luxonSlotTime[1] + 'mins';
  }
  ngAfterViewInit(){
    this.print.nativeElement.click();
  }

  @HostListener("document:click", ['$event.target'])
  showMessage(event)
  {    
    if (!this.formDialog.nativeElement.contains(event))
    {
      if( this.data?.clicked) {
        this.data.clicked=false;
      }
      else{
        this.dialogRef.close();
      }
    }
  }

  ngOnDestroy() {
    if(this.sub) {
      this.sub.unsubscribe();
    }
  }
}
